import * as React from "react";
import NextLink from "next/link";
import { addUtmSource, isCrossDomainHref } from "./utils/url";

export interface SmartLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  target?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  noFollow?: boolean;
}

/**
 * Smart Link component that automatically determines whether to use Next.js Link or a regular anchor tag
 * based on the href type (internal vs external, tel:, mailto:, etc.)
 */
export const Link = React.forwardRef<HTMLAnchorElement, SmartLinkProps>(
  (
    {
      href,
      children,
      className,
      target,
      noFollow = false,
      style,
      onClick,
      ...props
    },
    ref,
  ) => {
    const isSpecialProtocol =
      href.startsWith("tel:") ||
      href.startsWith("mailto:") ||
      href.startsWith("sms:");

    const isCrossDomain = isCrossDomainHref(href);

    const shouldUseAnchor = isSpecialProtocol || isCrossDomain;

    if (shouldUseAnchor) {
      const outboundHref = isSpecialProtocol ? href : addUtmSource(href);
      return (
        <a
          ref={ref}
          href={outboundHref}
          className={className}
          target={"_blank"}
          rel={`noopener noreferrer${noFollow ? " nofollow" : ""}`}
          style={style}
          onClick={onClick}
          {...props}
        >
          {children}
        </a>
      );
    }

    return (
      <NextLink
        ref={ref}
        href={href}
        className={className}
        target={target}
        style={style}
        rel={noFollow ? "nofollow" : undefined}
        onClick={onClick}
        {...props}
      >
        {children}
      </NextLink>
    );
  },
);

Link.displayName = "Link";
